import React, { useEffect, useState } from 'react';
import confetti from 'canvas-confetti';
import { FaTrophy, FaBrain, FaChartLine, FaTimes } from 'react-icons/fa';
import { useSelector } from 'react-redux';

const CircularProgress = ({ percentage }) => {
  // Calculate the circumference of the circle
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  
  // Calculate the stroke-dashoffset based on the percentage
  const offset = circumference - (percentage / 100) * circumference;
  
  return (
    <div className="relative inline-flex items-center justify-center">
      <svg className="w-28 h-28" viewBox="0 0 100 100">
        {/* Background circle */}
        <circle 
          className="text-butter-100" 
          strokeWidth="10" 
          stroke="currentColor" 
          fill="transparent" 
          r={radius} 
          cx="50" 
          cy="50" 
        />
        
        {/* Progress circle */}
        <circle 
          className="text-butter-500 transform -rotate-90 origin-center" 
          strokeWidth="10" 
          strokeDasharray={circumference} 
          strokeDashoffset={offset} 
          strokeLinecap="round" 
          stroke="currentColor" 
          fill="transparent" 
          r={radius} 
          cx="50" 
          cy="50" 
        />
      </svg>
      
      {/* Content in the middle - only percentage */}
      <div className="absolute flex items-center justify-center">
        <span className="text-2xl font-bold text-butter-700">{percentage}%</span>
      </div>
    </div>
  );
};

const MasteryCelebration = ({ onClose, basket }) => {
  // Start with visible true immediately
  const [visible, setVisible] = useState(true);
  const allBaskets = useSelector(state => state.workspaces.currentWorkspace.baskets || []);
  
  // Calculate how many baskets are fully mastered
  const masteredBaskets = allBaskets.filter(b => 
    b.topics?.length > 0 && b.topics.every(t => t.is_master)
  ).length;
  
  // Calculate overall workspace mastery based on baskets (not topics)
  const overallMasteryPercentage = allBaskets.length > 0 
    ? Math.round((masteredBaskets / allBaskets.length) * 100) 
    : 0;

  useEffect(() => {
    // Trigger confetti immediately when component mounts
    const triggerConfetti = () => {
      const duration = 2000;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };
      const randomInRange = (min, max) => Math.random() * (max - min) + min;
      
      // Launch multiple confetti bursts immediately
      for (let i = 0; i < 3; i++) {
        confetti({
          ...defaults,
          particleCount: 50,
          origin: { x: randomInRange(0.2, 0.4), y: Math.random() - 0.2 }
        });
        
        confetti({
          ...defaults,
          particleCount: 50,
          origin: { x: randomInRange(0.6, 0.8), y: Math.random() - 0.2 }
        });
      }
      
      // Continue with periodic confetti
      const animationEnd = Date.now() + duration;
      const interval = setInterval(() => {
        const timeLeft = animationEnd - Date.now();
        
        if (timeLeft <= 0) {
          return clearInterval(interval);
        }
        
        const particleCount = 30 * (timeLeft / duration);
        
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
        });
        
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
        });
      }, 250);
      
      return interval;
    };
    
    // Start confetti immediately
    const interval = triggerConfetti();
    
    return () => {
      clearInterval(interval);
    };
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 300); // Reduced fade-out time
  };

  if (!visible) return null;
  
  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50 transition-opacity duration-300 p-3">
      <div 
        className="bg-white rounded-xl border-2 border-neutral-900 p-3 sm:p-5 max-w-xs w-full text-center transform transition-all duration-300 animate-bounce-once relative"
        style={{ animation: 'bounce 0.8s ease 1' }} // Reduced animation time
      >
        {/* Close button */}
        <button 
          onClick={handleClose}
          className="absolute top-1 right-1 text-neutral-500 hover:text-neutral-800 transition-colors duration-200"
          aria-label="Close celebration"
        >
          <FaTimes className="w-5 h-5" />
        </button>
        
        <div className="flex justify-center mb-2">
          <div className="relative">
            <FaTrophy className="w-12 h-12 sm:w-16 sm:h-16 text-yellow-500" />
            <FaBrain className="w-5 h-5 sm:w-6 sm:h-6 text-butter-600 absolute -top-1 -right-1" />
          </div>
        </div>
        
        <h2 className="text-lg sm:text-xl font-bold text-neutral-900 mb-1">
          Congratulations!
        </h2>
        
        <p className="text-sm sm:text-base text-neutral-700 mb-3">
          You've mastered "{basket?.name}"!
        </p>
        
        <div className="bg-butter-50 rounded-lg p-2 sm:p-3 mb-3">
          <div className="flex items-center justify-center gap-1 mb-2">
            <FaChartLine className="text-butter-600 w-4 h-4" />
            <span className="font-semibold text-butter-800 text-sm">Workspace Progress</span>
          </div>
          
          <div className="flex items-center justify-center gap-4">
            {/* Circular progress indicator with only percentage */}
            <CircularProgress percentage={overallMasteryPercentage} />
            
            <div className="text-left">
              <p className="text-xs text-neutral-600 mb-1">
                <span className="font-medium text-butter-700">{masteredBaskets}</span> of <span className="font-medium">{allBaskets.length}</span>
              </p>
              <p className="text-xs text-neutral-600">
                baskets mastered
              </p>
            </div>
          </div>
        </div>
        
        <button
          onClick={handleClose}
          className="w-full px-4 py-1.5 bg-cranberry-600 text-white rounded-lg hover:bg-cranberry-700 transition-colors duration-300 text-sm"
        >
          Continue Learning
        </button>
      </div>
    </div>
  );
};

export default MasteryCelebration; 